/**
 * Full-race Timeline assembly: preallocated frame-major arrays that decoded UCT2 chunks are copied into
 * (03 §1). Pure (no DOM); used by the loader on the main thread and by the node tests.
 */
import type { Manifest, Timeline } from '../api.ts';
import type { TimelineFieldName } from './manifest.gen.ts';
import type { DecodedArray } from './chunks.ts';

type ArrayKey = Exclude<keyof Timeline, 'hz' | 'frames' | 'nCars' | 'lapM' | 's'>;

const KEY_BY_NAME: Record<TimelineFieldName, ArrayKey> = {
  lat: 'lat', lat_model: 'latModel', dist: 'dist', hdg: 'hdg', speed: 'speed', thr: 'thr', rpm: 'rpm',
  gear: 'gear', flags: 'flags', pos: 'pos', gap: 'gap', int: 'int', lapsdn: 'lapsdn', lap: 'lap', cmp: 'cmp',
  tyre: 'tyre', stint: 'stint', pits: 'pits',
};

/** Python-style float modulo (result has the sign of the divisor), as numpy `%` / np.mod. */
export function pyMod(a: number, b: number): number {
  const m = a % b;
  return m !== 0 && (m < 0) !== (b < 0) ? m + b : m;
}

export interface TimelineAssembler {
  timeline: Timeline;
  /** field id → Timeline key, from manifest.timeline.fields */
  keyById: ReadonlyMap<number, ArrayKey>;
  /** exact f64 scales by field id (decode with these, never the f32 copy in the chunk) */
  scales: ReadonlyMap<number, number>;
  /** copy one decoded chunk (chunk-local frame-major arrays) into the full-race arrays and derive s */
  apply(f0: number, n: number, fields: ReadonlyMap<number, DecodedArray>): void;
}

export function createTimeline(manifest: Manifest): TimelineAssembler {
  const m = manifest.timeline;
  if (m.hz !== 10) throw new Error(`timeline hz ${String(m.hz)} != 10`);
  const F = m.frames, C = m.n_cars, N = F * C;
  const keyById = new Map<number, ArrayKey>();
  const scales = new Map<number, number>();
  for (const f of m.fields) {
    keyById.set(f.id, KEY_BY_NAME[f.name]);
    scales.set(f.id, f.scale);
  }
  const timeline: Timeline = {
    hz: 10, frames: F, nCars: C, lapM: manifest.track.lap_m,
    dist: new Float64Array(N), s: new Float32Array(N), lat: new Float32Array(N), hdg: new Float32Array(N),
    speed: new Float32Array(N), thr: new Float32Array(N), rpm: new Float32Array(N),
    gap: new Float32Array(N), int: new Float32Array(N),
    gear: new Uint8Array(N), flags: new Uint16Array(N), pos: new Uint8Array(N), lapsdn: new Uint8Array(N),
    lap: new Uint8Array(N), cmp: new Uint8Array(N), tyre: new Uint8Array(N), stint: new Uint8Array(N),
    pits: new Uint8Array(N),
  };
  if (m.fields.some((f) => f.name === 'lat_model')) timeline.latModel = new Float32Array(N);
  const lapM = timeline.lapM;

  function apply(f0: number, n: number, fields: ReadonlyMap<number, DecodedArray>): void {
    if (f0 + n > F) throw new Error(`chunk frames ${String(f0)}+${String(n)} exceed timeline ${String(F)}`);
    for (const [id, src] of fields) {
      const key = keyById.get(id);
      if (!key) continue;                       // unknown / future field: ignore
      const dst = timeline[key];
      if (!dst) continue;
      if (src.length !== n * C) throw new Error(`field ${String(id)}: ${String(src.length)} values, expected ${String(n * C)}`);
      dst.set(src, f0 * C);
    }
    const { dist, s } = timeline;
    for (let j = f0 * C, end = (f0 + n) * C; j < end; j++) s[j] = pyMod(dist[j] ?? 0, lapM);
  }
  return { timeline, keyById, scales, apply };
}
